import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  Snackbar
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { fetchProjects, createProject, updateProject, deleteProject, fetchClients } from '../services/api';
import { fetchProjectTimelines, updateProjectTimeline } from '../services/api';

const ProjectList = () => {
  const [groups, setGroups] = useState([]);
  const [clients, setClients] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    clientId: '',
    projectName: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false);
  const [timelineForm, setTimelineForm] = useState({ id: '', projectName: '', startDate: '', endDate: '' });
  const [timelines, setTimelines] = useState([]);

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      const [projectsData, clientsData, timelinesData] = await Promise.all([
        fetchProjects(),
        fetchClients(),
        fetchProjectTimelines()
      ]);
      setGroups(projectsData);
      setClients(clientsData);
      setTimelines(timelinesData);
    } catch (error) {
      console.error('Error loading data:', error);
      showSnackbar('Failed to load data', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        clientId: project.client_id,
        projectName: project.project_name
      });
    } else {
      setEditingProject(null);
      setFormData({
        clientId: '',
        projectName: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProject(null);
    setFormData({
      clientId: '',
      projectName: ''
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.clientId || !formData.projectName) {
        showSnackbar('Please fill in all required fields', 'error');
        return;
      }

      if (editingProject) {
        await updateProject(editingProject.id, {
          clientId: formData.clientId,
          projectName: formData.projectName
        });
        showSnackbar('Project updated successfully');
      } else {
        await createProject({
          clientId: formData.clientId,
          projectName: formData.projectName
        });
        showSnackbar('Project created successfully');
      }

      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Error saving project:', error);
      showSnackbar(error.message || 'Failed to save project', 'error');
    }
  };

  const handleDelete = async (projectId) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await deleteProject(projectId);
        showSnackbar('Project deleted successfully');
        loadData();
      } catch (error) {
        console.error('Error deleting project:', error);
        showSnackbar(error.message || 'Failed to delete project', 'error');
      }
    }
  };

  const handleOpenTimelineDialog = async (project) => {
    // Fetch the timeline for this project
    const timelines = await fetchProjectTimelines();
    const timeline = timelines.find(t => t.project_name === project.project_name);
    setTimelineForm({
      id: timeline ? timeline.id : '',
      projectName: project.project_name,
      startDate: timeline ? timeline.start_date : '',
      endDate: timeline ? timeline.end_date : '',
    });
    setTimelineDialogOpen(true);
  };

  const handleCloseTimelineDialog = () => {
    setTimelineDialogOpen(false);
    setTimelineForm({ id: '', projectName: '', startDate: '', endDate: '' });
  };

  const handleTimelineInputChange = (field, value) => {
    setTimelineForm(prev => ({ ...prev, [field]: value }));
  };

  const handleTimelineSave = async () => {
    if (!timelineForm.startDate || !timelineForm.endDate) {
      showSnackbar('Please provide both start and end dates for the timeline.', 'error');
      return;
    }
    if (timelineForm.startDate >= timelineForm.endDate) {
      showSnackbar('Start date must be before end date.', 'error');
      return;
    }
    try {
      await updateProjectTimeline(timelineForm.id, {
        projectName: timelineForm.projectName,
        startDate: timelineForm.startDate,
        endDate: timelineForm.endDate,
        status: 'active',
        phases: [],
        milestones: [],
      });
      showSnackbar('Project timeline updated successfully');
      handleCloseTimelineDialog();
      loadData();
    } catch (error) {
      showSnackbar(error.message || 'Failed to update project timeline', 'error');
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Project List</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Project
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Client Name</TableCell>
                <TableCell>Project Name</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group, idx) => (
                group.projects.map((project, jdx) => {
                  const timeline = timelines.find(t => t.project_name === project.project_name);
                  return (
                    <TableRow key={project.id}>
                      <TableCell>{jdx === 0 ? group.client_name : ''}</TableCell>
                      <TableCell>{project.project_name}</TableCell>
                      <TableCell>{timeline ? timeline.start_date : '-'}</TableCell>
                      <TableCell>{timeline ? timeline.end_date : '-'}</TableCell>
                      <TableCell>
                        <Tooltip title="Edit Project">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog({
                              id: project.id,
                              client_id: project.client_id,
                              project_name: project.project_name
                            })}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Timeline">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenTimelineDialog(project)}
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Project">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(project.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Project Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingProject ? 'Edit Project' : 'Add New Project'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Client *</InputLabel>
              <Select
                value={formData.clientId}
                onChange={(e) => handleInputChange('clientId', e.target.value)}
                label="Client *"
              >
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.company_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Project Name *"
              value={formData.projectName}
              onChange={(e) => handleInputChange('projectName', e.target.value)}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingProject ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Timeline Edit Dialog */}
      <Dialog open={timelineDialogOpen} onClose={handleCloseTimelineDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Project Timeline</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Project Name"
              value={timelineForm.projectName}
              disabled
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              name="startDate"
              value={timelineForm.startDate}
              onChange={e => handleTimelineInputChange('startDate', e.target.value)}
              required
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="date"
              label="End Date"
              name="endDate"
              value={timelineForm.endDate}
              onChange={e => handleTimelineInputChange('endDate', e.target.value)}
              required
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTimelineDialog}>Cancel</Button>
          <Button onClick={handleTimelineSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectList; 